import { useSyncExternalStore } from 'react';

/** Device class the editor lays itself out for (spec §13, P11 Ruling 9). */
export type LayoutClass = 'phone' | 'tablet' | 'desktop';

/** Touches starting this close to a side edge belong to the system (iOS back swipe, Android back gesture). */
export const EDGE_GUTTER_PX = 16;

/** A coarse screen whose short side is under this is a phone, also turned sideways (P15: 863 × 360 is not a tablet). */
const PHONE_SHORT_SIDE_PX = 500;
/** Under this width the screen is a phone, whatever the pointer. */
const PHONE_MAX_WIDTH_PX = 640;
/** A coarse screen from the phone width up to this one is a tablet. */
const TABLET_MAX_WIDTH_PX = 1200;

/**
 * layoutOf as media queries, for CSS: the Tailwind variants `phone:` and `tablet:` in ui/globals.css repeat these
 * strings and app/layout.test.ts fails when the two drift. A phone turned sideways is still a phone. Media Queries 4
 * `or`, not a comma: Tailwind's @custom-variant splits its argument on commas.
 */
export const PHONE_MEDIA = `((width < ${PHONE_MAX_WIDTH_PX}px) or ((pointer: coarse) and (height < ${PHONE_SHORT_SIDE_PX}px)))`;
export const TABLET_MEDIA = `((pointer: coarse) and (width >= ${PHONE_MAX_WIDTH_PX}px) and (width < ${TABLET_MAX_WIDTH_PX}px) and (height >= ${PHONE_SHORT_SIDE_PX}px))`;

/** Phone under 640 px or a coarse screen under 500 px high; tablet from 640 to 1199 px with a coarse pointer; desktop otherwise. */
export function layoutOf(width: number, coarse: boolean, height = Infinity): LayoutClass {
  if (width < PHONE_MAX_WIDTH_PX || (coarse && height < PHONE_SHORT_SIDE_PX)) return 'phone';
  return coarse && width < TABLET_MAX_WIDTH_PX ? 'tablet' : 'desktop';
}

const coarseQuery = () => (typeof window.matchMedia === 'function' ? window.matchMedia('(pointer: coarse)') : null);

const current = (): LayoutClass => layoutOf(window.innerWidth, coarseQuery()?.matches ?? false, window.innerHeight);

function subscribe(cb: () => void): () => void {
  const q = coarseQuery();
  window.addEventListener('resize', cb);
  q?.addEventListener?.('change', cb);
  return () => {
    window.removeEventListener('resize', cb);
    q?.removeEventListener?.('change', cb);
  };
}

/** The current layout class, updated on resize and when the primary pointer changes (keyboard attached, etc.). */
export function useLayout(): LayoutClass {
  return useSyncExternalStore(subscribe, current, () => 'desktop');
}

/** Whether the primary pointer is a finger, read once (the editor's mobile gestures are decided when it opens). */
export const isCoarsePointer = (): boolean => coarseQuery()?.matches ?? false;

/** Whether the primary pointer is a finger (controls are 44 px then, spec §13). */
export function useCoarsePointer(): boolean {
  return useSyncExternalStore(subscribe, () => coarseQuery()?.matches ?? false, () => false);
}
